import { useState } from "react";
import { craftDiaryPage } from "@/mocks/craftDiary";
import GlobalBottomCta from "@/components/feature/GlobalBottomCta";
import PageMeta from "@/components/feature/PageMeta";
import ScrollReveal from "@/components/feature/ScrollReveal";

const pageMeta = {
  title: "職人日誌｜來加綉綉紅豆餅",
  description: "來加綉綉職人日誌分享紅豆餅製作技法、內餡搭配、回烤方式、市集經驗、加盟心得與品牌經營觀點。",
  keywords: "紅豆餅製作,紅豆餅職人,車輪餅製作技法,手作紅豆餅,紅豆餅內餡,紅豆餅回烤,紅豆餅加盟心得",
  ogType: "article",
};

const categoryColors: Record<string, string> = {
  craft: "bg-redbean-primary text-redbean-cream",
  filling: "bg-redbean-caramel text-white",
  reheat: "bg-amber-600 text-white",
  market: "bg-redbean-gold text-redbean-coffee",
  franchise: "bg-redbean-coffee text-redbean-cream",
  brand: "bg-redbean-primary/80 text-redbean-cream",
};

const categoryIcons: Record<string, string> = {
  craft: "ri-fire-line",
  filling: "ri-heart-3-line",
  reheat: "ri-snowflake-line",
  market: "ri-store-2-line",
  franchise: "ri-briefcase-4-line",
  brand: "ri-book-open-line",
};

export default function CraftDiary() {
  const { hero, categories, articles } = craftDiaryPage;
  const [activeCat, setActiveCat] = useState("all");

  const filtered =
    activeCat === "all"
      ? articles
      : articles.filter((a) => a.category === activeCat);

  return (
    <main className="pt-16 md:pt-20">
      <PageMeta {...pageMeta} />
      {/* Hero */}
      <section className="relative min-h-[360px] md:min-h-[440px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-redbean-primary/40">
          <img
            src="https://readdy.ai/api/search-image?query=artisan%20hands%20shaping%20taiyaki%20red%20bean%20cake%20dough%20on%20traditional%20griddle%20warm%20workshop%20lighting%20flour%20dusting%20close-up%20documentary%20photography%20cozy%20handmade%20atmosphere%20Taiwanese%20street%20food%20craftsman%20warm%20amber%20tones%20cream%20background&width=1600&height=900&seq=cd-hero-1&orientation=landscape"
            alt="手作紅豆餅職人日誌製作過程"
            className="w-full h-full object-cover object-center"
            loading="eager"
            decoding="async"
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-redbean-coffee/55 via-redbean-coffee/45 to-redbean-coffee/60" />
        </div>
        <div className="relative z-10 mx-auto px-4 md:px-6 lg:px-8 text-center" style={{ maxWidth: "900px" }}>
          <span className="inline-block px-4 py-1.5 bg-redbean-gold text-redbean-coffee text-sm font-semibold rounded-full mb-6">
            {hero.tag}
          </span>
          <h1
            className="font-heading font-black text-redbean-cream mb-5 text-balance"
            style={{ fontSize: "clamp(2rem, 5vw, 2.75rem)", lineHeight: 1.3 }}
          >
            {hero.title}
          </h1>
          <p className="text-redbean-cream/85 max-w-xl mx-auto" style={{ fontSize: "1.1875rem", lineHeight: 1.75 }}>
            {hero.subtitle}
          </p>
        </div>
      </section>

      {/* Filter + Articles */}
      <section className="py-12 md:py-16 bg-redbean-cream">
        <div className="mx-auto px-4 md:px-6 lg:px-8" style={{ maxWidth: "1200px" }}>
          {/* Category Tabs */}
          <ScrollReveal>
            <div className="flex flex-wrap justify-center gap-2 mb-10 md:mb-12">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => setActiveCat(cat.id)}
                  className={`inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full text-sm font-semibold transition-all whitespace-nowrap ${
                    activeCat === cat.id
                      ? "bg-redbean-primary text-redbean-cream"
                      : "bg-white text-redbean-coffee hover:bg-redbean-lightcream shadow-soft"
                  }`}
                >
                  {cat.id !== "all" && (
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className={`${categoryIcons[cat.id] || "ri-file-text-line"}`} />
                    </span>
                  )}
                  {cat.label}
                </button>
              ))}
            </div>
          </ScrollReveal>

          {/* Articles Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {filtered.map((article, idx) => (
              <ScrollReveal key={article.id} delay={idx * 100}>
                <div className="bg-white rounded-card p-6 shadow-soft hover:shadow-card transition-shadow duration-300 group">
                  <span
                    className={`inline-block px-3 py-1 text-xs font-semibold rounded-full mb-4 ${
                      categoryColors[article.category] || "bg-redbean-gold text-redbean-coffee"
                    }`}
                  >
                    {article.categoryLabel}
                  </span>
                  <h3
                    className="font-heading font-bold text-redbean-coffee mb-3 group-hover:text-redbean-primary transition-colors"
                    style={{ fontSize: "1.25rem", lineHeight: 1.5 }}
                  >
                    {article.title}
                  </h3>
                  <p className="text-redbean-coffee/60 text-[1rem] leading-relaxed mb-5">
                    {article.summary}
                  </p>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 text-redbean-caramel text-sm font-medium hover:text-redbean-primary transition-colors"
                  >
                    閱讀更多
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-line" />
                    </span>
                  </button>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      <GlobalBottomCta />
    </main>
  );
}